import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import EmptyStateIllustration from '@/components/EmptyStateIllustration'
import { Center } from '@/components/ui/center'
import useJiriHeaderItems from '@/modules/jiri/useJiriHeaderItems'
import useProfileHeaderItems from '@/modules/profile/useProfileHeaderItems'
import TradeMerchantEmpty from '@/modules/trade/TradeMerchantEmpty'
import TradeOrders from '@/modules/trade/TradeOrders'
import {
  fetchTradeOrdersThunk,
  fetchTradeStatsThunk,
  restoreTradeMerchantType,
  selectCanAccessTrade,
  selectTradeMerchantType,
} from '@/redux/slices/tradeSlice'
import { useAppDispatch, useAppSelector, type RootState } from '@/redux/store'
import { UserRole } from '@/typings/domain'
import { Stack, useFocusEffect, useRouter } from 'expo-router'
import { useCallback, useEffect, useRef } from 'react'
import { useStore } from 'react-redux'

export default function TradeOrdersPage() {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const jiriHeaderItems = useJiriHeaderItems()
  const profileHeaderItems = useProfileHeaderItems()

  const store = useStore<RootState>()
  const currentUserLoaded = useAppSelector(state => state.currentUser.loaded)
  const canAccessTrade = useAppSelector(selectCanAccessTrade)
  const isOutworker = useAppSelector(state => state.currentUser.user?.role === UserRole.Outworker)
  const merchantTypeRestored = useAppSelector(state => state.trade.merchantTypeRestored)
  const merchantType = useAppSelector(selectTradeMerchantType)
  const duration = useAppSelector(state => state.trade.duration)
  const paidStatus = useAppSelector(state => state.trade.paidStatus)

  const billingAccountId = useAppSelector(state => state.currentUser.user?.billingAccount?.id)
  useEffect(() => {
    dispatch(restoreTradeMerchantType())
  }, [dispatch, billingAccountId])

  const ready = merchantTypeRestored && canAccessTrade && !!merchantType

  // Single load path: runs on focus and again whenever the query changes while focused, so a change
  // made on another screen (e.g. switch_merchant) loads exactly once when this screen regains focus.
  const lastQueryKey = useRef<string | null>(null)
  useFocusEffect(
    useCallback(() => {
      if (!ready) return
      const queryKey = `${merchantType}|${duration}|${paidStatus}`
      const queryChanged = queryKey !== lastQueryKey.current
      lastQueryKey.current = queryKey
      // Coming back from an order's detail must not collapse an already paginated list to page 1.
      if (queryChanged || store.getState().trade.currentPage <= 1) {
        dispatch(fetchTradeOrdersThunk({ page: 1 }))
      }
      dispatch(fetchTradeStatsThunk())
    }, [dispatch, store, ready, merchantType, duration, paidStatus])
  )

  const handleSwitchMerchant = () => router.push('/trade/switch_merchant')

  if (currentUserLoaded && !canAccessTrade) {
    return (
      <>
        <Stack.Screen options={{ ...buildScreenHeaderItems({ left: profileHeaderItems, right: jiriHeaderItems }) }} />
        {isOutworker ? (
          <Center className="flex-1">
            <EmptyStateIllustration message="你没有查看交易的权限" />
          </Center>
        ) : (
          <TradeMerchantEmpty />
        )}
      </>
    )
  }

  return (
    <>
      <Stack.Screen
        options={{
          ...buildScreenHeaderItems({
            left: profileHeaderItems,
            right: [
              {
                type: 'button',
                label: '切换商户号',
                icon: { type: 'sfSymbol', name: 'arrow.left.arrow.right' },
                onPress: handleSwitchMerchant,
              },
              ...jiriHeaderItems,
            ],
          }),
        }}
      />

      <TradeOrders />
    </>
  )
}
